import { useEffect, useMemo, useRef, useState, type ClipboardEvent } from 'react'
import { AnimatePresence } from 'framer-motion'
import type { Category, Recipe } from '../../lib/supabase'
import {
  blankIngredient, blankStep, draftFromRecipe, emptyDraft, LIMITS, move, recipeFromDraft, splitLine,
  type DraftErrors, type DraftIngredient, type RecipeDraft, type RecipeFields,
} from '../../lib/recipeDraft'
import { ingredientCategory } from '../../lib/recipeBook'
import type { KnownName } from '../../lib/catalog'
import { resizePhoto } from '../../lib/photo'
import type { PhotoChange, SaveOutcome } from '../../hooks/useRecipes'
import { SheetPanel } from '../SheetPanel'
import { ArrowUpIcon, CloseIcon, PlusIcon } from '../icons'
import { IngredientRow } from './IngredientRow'

export interface RecipeEditorProps {
  open: boolean
  /** The recipe to edit; none = a new one written by hand. */
  recipe?: Recipe | null
  /** Its current photo (useRecipePhotos). */
  photoUrl?: string | null
  locale?: string
  categories: Category[]
  /** Where the household would file a name (App's categoryFor: memory, then keywords). */
  categoryFor: (name: string) => string | null
  tintFor: (categoryId: string | null) => string
  /** The household's names, list first (lib/catalog.ts knownNames), for the name fields. */
  known: KnownName[]
  /** useRecipes.saveRecipe, bound to the recipe's id (or null). */
  onSave: (fields: RecipeFields, photo: PhotoChange) => Promise<SaveOutcome>
  /** After a save: the recipe's id, e.g. to open it. */
  onSaved?: (id: string) => void
  /** A page the recipe was read from only in part (no ingredient list on it): its text, to copy from. */
  sourceText?: string
  onClose: () => void
}

const SAVE_ERRORS: Record<Exclude<SaveOutcome, { ok: true }>['error'], string> = {
  photo_failed: 'The photo didn’t upload. Try again, or save without it.',
  failed: 'Couldn’t save the recipe. Check your connection and try again.',
  subscription_required: 'Recipes are part of Plus. Get Plus to change them.',
}

// Writing a recipe by hand, or changing any part of one: photo, name, servings,
// time, ingredients (rows like list items: category, name, amount —
// lib/recipeDraft.ts, components/recipes/IngredientRow) and the method. The body mounts only while open, so a
// cancelled edit leaves nothing behind.
export function RecipeEditor(props: RecipeEditorProps) {
  return <AnimatePresence>{props.open && <Body {...props} />}</AnimatePresence>
}

function Body(p: RecipeEditorProps) {
  const [draft, setDraft] = useState<RecipeDraft>(() => (p.recipe
    ? draftFromRecipe(p.recipe, p.locale, g => ingredientCategory(g, p.categoryFor, p.categories))
    : emptyDraft()))
  const [photo, setPhoto] = useState<PhotoChange>({ kind: 'keep' })
  const [errors, setErrors] = useState<DraftErrors>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [pickingKey, setPickingKey] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const sourceRef = useRef<HTMLTextAreaElement>(null)
  const [sourceHint, setSourceHint] = useState<string | null>(null)

  // The picked photo's preview, released when it is replaced or the sheet closes.
  const preview = useMemo(() => (photo.kind === 'set' ? URL.createObjectURL(photo.blob) : null), [photo])
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  const shownPhoto = photo.kind === 'set' ? preview : photo.kind === 'remove' ? null : p.photoUrl ?? null
  const set = (patch: Partial<RecipeDraft>) => setDraft(d => ({ ...d, ...patch }))

  async function pick(file: File | undefined) {
    if (!file) return
    setFailure(null)
    const res = await resizePhoto(file)
    if (!res.ok) { setFailure(res.error === 'too_large' ? 'That photo is too large.' : 'That file isn’t a photo we can read.'); return }
    setPhoto({ kind: 'set', blob: res.blob })
  }

  const patch = (key: string, change: (g: DraftIngredient) => Partial<DraftIngredient>) =>
    setDraft(d => ({ ...d, ingredients: d.ingredients.map(g => (g.key === key ? { ...g, ...change(g) } : g)) }))

  // A new name is filed again the way the list would, until a member picks the category.
  const named = (g: DraftIngredient, name: string): Partial<DraftIngredient> =>
    ({ name, ...(g.categoryPicked ? {} : { categoryId: p.categoryFor(name) }) })

  function setName(key: string, name: string) {
    setActiveKey(key)
    patch(key, g => named(g, name))
  }

  const linesOf = (text: string) => text.split(/\r?\n/).map(l => l.replace(/^\s*[-•*]\s*/, '').trim()).filter(Boolean)

  // Lines as ingredient rows, each split into name and amount and filed: in
  // place of the row at `key` when it is still empty, else after it (or at the end).
  function insertLines(lines: string[], key: string | null) {
    const at = key ? draft.ingredients.findIndex(g => g.key === key) : draft.ingredients.length - 1
    const current = draft.ingredients[at] as DraftIngredient | undefined
    const empty = !!current && !current.name.trim() && !current.amount.trim()
    const rows = lines.map((line, i): DraftIngredient => {
      const { name, amount } = splitLine(line, p.locale)
      const base = i === 0 && empty ? current : blankIngredient()
      return { ...base, name, amount, categoryId: p.categoryFor(name), categoryPicked: false }
    })
    const keep = current && !empty ? [current] : []
    set({ ingredients: [...draft.ingredients.slice(0, Math.max(at, 0)), ...keep, ...rows, ...draft.ingredients.slice(at + 1)] })
    setActiveKey(null)
  }

  // Pasting a whole ingredient list into one row: one row per line.
  function pasteLines(key: string, e: ClipboardEvent<HTMLInputElement>) {
    const lines = linesOf(e.clipboardData.getData('text'))
    if (lines.length < 2) return
    e.preventDefault()
    insertLines(lines, key)
  }

  // The page's text: the lines the member selected become ingredients.
  function takeSelection() {
    const el = sourceRef.current
    const lines = el ? linesOf(el.value.slice(el.selectionStart, el.selectionEnd)) : []
    if (!lines.length) { setSourceHint('Select the ingredient lines in the text first.'); return }
    setSourceHint(null)
    insertLines(lines, null)
  }

  function addIngredient(after?: string) {
    const row = blankIngredient()
    const at = after ? draft.ingredients.findIndex(g => g.key === after) + 1 : draft.ingredients.length
    set({ ingredients: [...draft.ingredients.slice(0, at), row, ...draft.ingredients.slice(at)] })
    setFocusKey(row.key)
  }

  function addStep() {
    const row = blankStep()
    set({ steps: [...draft.steps, row] })
    setFocusKey(row.key)
  }

  async function save() {
    const res = recipeFromDraft(draft)
    if (!res.ok) { setErrors(res.errors); return }
    setErrors({})
    setFailure(null)
    setSaving(true)
    const outcome = await p.onSave(res.fields, photo)
    setSaving(false)
    if (!outcome.ok) { setFailure(SAVE_ERRORS[outcome.error]); return }
    p.onSaved?.(outcome.id)
    p.onClose()
  }

  const autoFocus = (key: string) => (focusKey === key ? (el: HTMLElement | null) => { if (el) { el.focus(); setFocusKey(null) } } : undefined)

  return (
    <SheetPanel onClose={p.onClose} maxHeight="94dvh" layout="scroll" ariaLabel={p.recipe ? 'Edit recipe' : 'New recipe'} zIndex={14}>
      <form className="ml-editor" onSubmit={e => { e.preventDefault(); save() }} noValidate>
        <header className="ml-editor__head">
          <button type="button" className="ml-btn ml-btn--secondary" onClick={p.onClose}>Cancel</button>
          <h2 className="ml-display ml-editor__title">{p.recipe ? 'Edit recipe' : 'New recipe'}</h2>
          <button type="submit" className="ml-btn ml-btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </header>

        {p.sourceText && (
          <section className="ml-editor__source" aria-label="From the page">
            <p className="ml-editor__source-note">
              That page has no ingredient list we could read. Here is its text: select the ingredient lines,
              then tap Use as ingredients.
            </p>
            <textarea ref={sourceRef} className="ml-input ml-editor__source-text" readOnly rows={8}
              value={p.sourceText} aria-label="The page’s text" />
            <button type="button" className="ml-btn ml-btn--secondary ml-editor__source-use" onClick={takeSelection}>
              Use as ingredients
            </button>
            {sourceHint && <span className="ml-caption" role="status">{sourceHint}</span>}
          </section>
        )}

        <div className="ml-editor__photo">
          {shownPhoto
            ? <img className="ml-editor__photo-img" src={shownPhoto} alt="The recipe’s photo" />
            : <div className="ml-editor__photo-empty">No photo. The cover is drawn from the ingredients.</div>}
          <div className="ml-editor__photo-actions">
            <button type="button" className="ml-btn ml-btn--secondary" onClick={() => fileRef.current?.click()}>
              {shownPhoto ? 'Change photo' : 'Add photo'}
            </button>
            {shownPhoto && (
              <button type="button" className="ml-text-btn ml-text-btn--destructive"
                onClick={() => setPhoto(p.recipe?.photo_path ? { kind: 'remove' } : { kind: 'keep' })}>
                Remove photo
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden aria-label="Choose a photo"
            onChange={e => { pick(e.target.files?.[0]); e.target.value = '' }} />
        </div>

        <label className="ml-field">
          <span className="ml-label">Name</span>
          <input className="ml-input" value={draft.title} maxLength={LIMITS.title} placeholder="Pyttipanna"
            aria-invalid={errors.title ? true : undefined} onChange={e => set({ title: e.target.value })} />
          {errors.title && <span className="ml-editor__error" role="alert">{errors.title}</span>}
        </label>

        <div className="ml-editor__pair">
          <label className="ml-field">
            <span className="ml-label">Servings</span>
            <input className="ml-input ml-num" inputMode="numeric" value={draft.servings} placeholder="Not given"
              aria-invalid={errors.servings ? true : undefined} onChange={e => set({ servings: e.target.value })} />
          </label>
          <label className="ml-field">
            <span className="ml-label">Minutes</span>
            <input className="ml-input ml-num" inputMode="numeric" value={draft.minutes} placeholder="Not given"
              aria-invalid={errors.minutes ? true : undefined} onChange={e => set({ minutes: e.target.value })} />
          </label>
        </div>
        {(errors.servings || errors.minutes) && (
          <span className="ml-editor__error" role="alert">{errors.servings ?? errors.minutes}</span>
        )}

        <fieldset className="ml-editor__group">
          <legend className="ml-label">Ingredients</legend>
          <p className="ml-caption">Name, then amount. The dot is its aisle; tap it to change. Mark what you usually have in as pantry.</p>
          <ul className="ml-editor__rows">
            {draft.ingredients.map((g, i) => (
              <IngredientRow key={g.key} g={g} index={i} categories={p.categories} known={p.known} tintFor={p.tintFor}
                active={activeKey === g.key} picking={pickingKey === g.key} nameRef={autoFocus(g.key)}
                onName={name => setName(g.key, name)}
                onChoose={name => { patch(g.key, x => named(x, name)); setActiveKey(null) }}
                onAmount={amount => patch(g.key, () => ({ amount }))}
                onPick={categoryId => { patch(g.key, () => ({ categoryId, categoryPicked: true })); setPickingKey(null) }}
                onTogglePicking={() => setPickingKey(k => (k === g.key ? null : g.key))}
                onTogglePantry={() => patch(g.key, x => ({ pantry: !x.pantry }))}
                onRemove={() => set({ ingredients: draft.ingredients.filter(x => x.key !== g.key) })}
                onNext={() => addIngredient(g.key)}
                onPaste={e => pasteLines(g.key, e)}
                onFocus={() => setActiveKey(g.key)} />
            ))}
          </ul>
          <button type="button" className="ml-text-btn ml-editor__add" onClick={() => addIngredient()}>
            <PlusIcon size={16} /> Add ingredient
          </button>
          {errors.ingredients && <span className="ml-editor__error" role="alert">{errors.ingredients}</span>}
        </fieldset>

        <fieldset className="ml-editor__group">
          <legend className="ml-label">Method</legend>
          <p className="ml-caption">A step that says “simmer 15 min” gets a timer in cooking mode.</p>
          <ol className="ml-editor__rows">
            {draft.steps.map((s, i) => (
              <li key={s.key} className="ml-editor__row ml-editor__row--step">
                <span className="ml-editor__num ml-num" aria-hidden="true">{i + 1}</span>
                <textarea ref={autoFocus(s.key)} className="ml-input ml-editor__step" rows={3} value={s.text}
                  maxLength={LIMITS.stepText} aria-label={`Step ${i + 1}`}
                  onChange={e => set({ steps: draft.steps.map(x => (x.key === s.key ? { ...x, text: e.target.value } : x)) })} />
                <span className="ml-editor__step-tools">
                  <button type="button" className="ml-editor__icon" aria-label={`Move step ${i + 1} up`} disabled={i === 0}
                    onClick={() => set({ steps: move(draft.steps, i, -1) })}>
                    <ArrowUpIcon size={18} />
                  </button>
                  <button type="button" className="ml-editor__icon ml-editor__icon--down" aria-label={`Move step ${i + 1} down`}
                    disabled={i === draft.steps.length - 1} onClick={() => set({ steps: move(draft.steps, i, 1) })}>
                    <ArrowUpIcon size={18} />
                  </button>
                  <button type="button" className="ml-editor__icon" aria-label={`Remove step ${i + 1}`}
                    onClick={() => set({ steps: draft.steps.filter(x => x.key !== s.key) })}>
                    <CloseIcon size={18} />
                  </button>
                </span>
              </li>
            ))}
          </ol>
          <button type="button" className="ml-text-btn ml-editor__add" onClick={addStep}>
            <PlusIcon size={16} /> Add step
          </button>
          {errors.steps && <span className="ml-editor__error" role="alert">{errors.steps}</span>}
        </fieldset>

        {failure && <p className="ml-editor__error" role="alert">{failure}</p>}
      </form>
    </SheetPanel>
  )
}
